import { Branch } from "@/app/branchs/branchs.type";
import { Select } from "@/shared/components/organisms/Select";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { t } from "i18next";
import {
  colorsKpi,
  configureExporting,
  generateYearsOptions,
  initializeAmChart,
  getMonthsOptions,
} from "@/shared/utils/chartUtils";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { useEffect, useState, useMemo } from "react";
import { Option } from "@/shared/components/molecules/OptionsSelect";
import ScrollableSelectContainer from "@/shared/components/organisms/ScrollableSelectContainer";
import { useGetExpensesBranchByDaysQuery } from "@/app/kpi/kpi.api";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type ExpensesBranchDaysChartProps = {
  branches: Branch[];
};

const ExpensesBranchDaysChart: React.FC<ExpensesBranchDaysChartProps> = ({
  branches,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  //const [branchSelected, setBranchSelected] = useState<Option>();
  const [branchesOptions, setBranchesOptions] = useState<Option[]>([]);
  //const [yearSelected, setYearSelected] = useState<Option>();
  //const [monthSelected, setMonthSelected] = useState<Option>();
  const [currencyEarningsTotalDays, setCurrencyEarningsTotalDays] =
    useState<Option>();
  const [currencyEarningsTotalOptions, setCurrencyEarningsTotalOptions] =
    useState<Option[]>([]);

  const monthsOptions = useMemo(() => getMonthsOptions(t), [t]);

  const yearsOptions = generateYearsOptions(
    new Date().getFullYear() - 10,
    new Date().getFullYear()
  );

  const [branchSelected, setBranchSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [monthSelected, setMonthSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("monthSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [yearSelected, setYearSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("yearSelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const { data: dataChart } = useGetExpensesBranchByDaysQuery({
    filial: branchSelected?.id,
    currency_request: currencyEarningsTotalDays?.id,
    year: yearSelected?.id,
    month: monthSelected?.id,
  });

  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (branchesOptions.length > 0 && !branchSelected) {
      setBranchSelected(branchesOptions[0]);
    }
    if (yearsOptions.length > 0 && !yearSelected) {
      const defaultYear = yearsOptions.find(year => year.id === currentYear);
      setYearSelected(defaultYear || yearsOptions[0]);
    }
    if (monthsOptions.length > 0 && !monthSelected) {
      const defaultMonth = monthsOptions.find(month => month.id === currentMonth);
      setMonthSelected(defaultMonth || monthsOptions[0]);
    }
  }, [branchesOptions, yearsOptions, monthsOptions, branchSelected, yearSelected, monthSelected]);

  useEffect(() => {
    if (dataChart) {
      console.log("aui", dataChart);
      const transformDataChart: any = [];
      dataChart.data.map((item: any) => {
        for (let index = 0; index < dataChart.days.length; index++) {
          if (!transformDataChart[index]) {
            transformDataChart[index] = {
              day: index + 1,
            };
          }
          transformDataChart[index][item.fee] = item.data[index] || 0;
          transformDataChart[index]["none"] = 0;
        }
      });

      const currencies = Object.keys(dataChart.currency).map((key) => ({
        id: key,
        name: dataChart.currency[key],
      }));

      setCurrencyEarningsTotalDays(currencies[0]);
      setCurrencyEarningsTotalOptions(currencies);

      createExpensesDaysChart(
        transformDataChart,
        colorsKpi,
        dataChart,
        dataChart.currency_request
      );
    }
  }, [dataChart]);

  function createExpensesDaysChart(
    data: any,
    colors: any,
    globalData: any,
    currency: any
  ) {
    am5.ready(function () {
      const root = initializeAmChart("chart_expenses_total_days");
  
      root.setThemes([am5themes_Animated.new(root)]);
  
      if (root._logo) {
        root._logo.dispose();
      }

      if (!data || data.length === 0) {
        console.log("No data available, displaying message.");
        root.container.children.push(
          am5.Label.new(root, {
            text: "Información no disponible",
            fontSize: 20,
            fill: am5.color("#cbcac8"),
            centerX: am5.p50,
            centerY: am5.p50,
            x: am5.p50,
            y: am5.p50,
            verticalCenter: "middle",
          })
        );

        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }
  
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelX: undefined,
          wheelY: undefined,
          paddingLeft: 0,
          paddingRight: 0,
          layout: root.verticalLayout,
        })
      );
  
      const legend = chart.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
          height: am5.percent(30),
          width: am5.percent(80),
          layout: am5.GridLayout.new(root, {
            fixedWidthGrid: true,
          }),
        })
      );
  
      // Configurar las etiquetas de la leyenda para que sean de color gris
      legend.labels.template.setAll({
        fontSize: 10,
        fontWeight: "300",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      const xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minGridDistance: 10,
        minorGridEnabled: true,
      });
  
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "day",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
  
      xRenderer.grid.template.setAll({
        location: 1,
        visible: false,
      });
  
      xAxis.data.setAll(data);
  
      // Configurar las etiquetas del eje X para que sean de color gris
      xAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          calculateTotals: true,
          min: 0,
          extraMax: 0.1,
          renderer: am5xy.AxisRendererY.new(root, {
            strokeOpacity: 0.1,
          }),
        })
      );
  
      // Configurar las etiquetas del eje Y para que sean de color gris
      yAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });
  
      function makeSeries(
        name: any,
        fieldName: any,
        showTotal: any,
        color: any
      ) {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: fieldName,
            categoryXField: "day",
            stacked: true,
            stoke: am5.color(0xffffff),
          })
        );
  
        series.columns.template.setAll({
          tooltipText: "{name}, {categoryX}: {valueY}" + currency,
          width: am5.percent(90),
          fill: am5.color(color),
          tooltipY: 0,
          strokeOpacity: 0, // Quitar la línea del borde
          strokeWidth: 0,
        });
  
        if (showTotal) {
          series.columns.template.setAll({
            fillOpacity: 0,
            strokeOpacity: 0,
          });
  
          series.bullets.push(function (root, series, dataItem) {
            if (dataItem.get("valueYTotal") !== 0) {
              return am5.Bullet.new(root, {
                locationY: 0.5,
                sprite: am5.Label.new(root, {
                  text: "{valueYTotal}",
                  fill: am5.color(0x000000),
                  centerY: am5.p100,
                  centerX: am5.p50,
                  populateText: true,
                  fontSize: "0.8rem",
                }),
              });
            }
          });
        }
  
        series.data.setAll(data);
        series.appear();
  
        if (!showTotal) {
          legend.data.push(series);
        }
      }
  
      globalData.data.forEach((item: any, index: any) => {
        makeSeries(item.fee, item.fee, false, colors[index]);
      });
      makeSeries("", "none", true, "#000000");
  
      chart.appear(1000, 100);

      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
  
      configureExporting(root, data);
    }); // end am5.ready()
  }

  return (
    <>
      {" "}
      <h2 className="text-center font-bold leading-6 text-gray-900">
        {t("user.views.main_title_expenses_total_diary")}
      </h2>
      <ScrollableSelectContainer>
        <Select
          name="branch"
          selected={branchSelected}
          setSelected={setBranchSelected}
          options={branchesOptions}
          tittle={t("user.views.branch")}
        />
        <Select
          name="currency"
          selected={currencyEarningsTotalDays}
          setSelected={currencyEarningsTotalDays}
          options={currencyEarningsTotalOptions}
          tittle={t("user.views.currency")}
        />
        <Select
          name="month"
          selected={monthSelected}
          setSelected={setMonthSelected}
          options={monthsOptions}
          tittle={t("user.views.month")}
        />
        <Select
          name="year"
          selected={yearSelected}
          setSelected={setYearSelected}
          options={yearsOptions}
          tittle={t("user.views.year")}
        />
      </ScrollableSelectContainer>
      <div style={{ position: "relative" }} className="xl:mt-4 md:mt-24 sm:mt-24">
        {isLoading && (
          <div className="flex justify-center items-center h-90 mt-20">
            <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
          </div>
        )}
        <div
          id="chart_expenses_total_days"
          style={{ width: "100%", height: "400px" }}
        ></div>
      </div>
    </>
  );
};

export default ExpensesBranchDaysChart;
